// src/hooks/useCollections.ts
// -----------------------------------------------------------------------------
// Derives the LIVE collection (brand) set from the tenant's catalogue, the same
// way useCategories derives the category set.
//
// The division of labour: shopConfig lists which brand tiles exist and what logo
// each one has; the catalogue decides which of them currently have something to
// sell. Config alone cannot know - it is a snapshot written by hand.
//
// Shares productsApi's in-flight and sessionStorage cache, so running alongside
// useCategories costs no extra request.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { shopConfig as cfg } from '../shopConfig';
import { fetchProducts } from '../productsApi';

const CACHE_KEY = `ss_collections_${cfg.gido_id}`;

export function useCollections(): string[] {
  const [live, setLive] = useState<string[]>(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return [];
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // view=grid carries the collections field and omits descriptions, specs,
        // options and variants - the same light shape useCategories uses.
        const list = await fetchProducts('grid');
        const distinct = Array.from(new Set(
          list
            .flatMap((p: any) => p.collections || [])
            .filter((c: string) => c && c.trim())
        )) as string[];
        if (!alive) return;
        setLive(distinct);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(distinct)); } catch { /* ignore */ }
      } catch { /* keep whatever we have; the strip just stays hidden */ }
    })();
    return () => { alive = false; };
  }, []);

  return live;
}